import { api } from '@packages/backend/convex/_generated/api';
import { useConvex } from 'convex/react';
import { useCallback, useMemo } from 'react';
import { type ApplePayAuthorizationResult, ApplePayButton } from './ApplePayButton';

const APPLE_PAY_BUTTON_VERSION = 14;
const APPLE_PAY_LABEL = 'OrderHub';
const APPLE_PAY_SUPPORTED_NETWORKS = [
  'visa',
  'masterCard',
  'amex',
] satisfies ApplePaySupportedNetwork[];
const APPLE_PAY_MERCHANT_CAPABILITIES = ['supports3DS'] satisfies ApplePayMerchantCapability[];

type SessionApplePayButtonProps = {
  sessionUuid: string;
  amountCents: number;
  onSuccess: () => void;
  onError: (message: string) => void;
  onSubmitting?: () => void;
  onCancel?: () => void;
};

const formatDollars = (cents: number): string => (cents / 100).toFixed(2);

const getWorldlineApplePayToken = (payment: ApplePayPayment) => {
  const { paymentData } = payment.token;
  const { data, header } = paymentData;

  if (!data) {
    throw new Error('Apple Pay token is missing encrypted payment data');
  }

  if (!header.ephemeralPublicKey || !header.publicKeyHash) {
    throw new Error('Apple Pay token is missing required header values');
  }

  return {
    encryptedPaymentData: data,
    ephemeralKey: header.ephemeralPublicKey,
    publicKeyHash: header.publicKeyHash,
  };
};

export const SessionApplePayButton = ({
  sessionUuid,
  amountCents,
  onSuccess,
  onError,
  onSubmitting,
  onCancel,
}: SessionApplePayButtonProps) => {
  const convex = useConvex();

  const paymentRequest = useMemo<ApplePayPaymentRequest>(
    () => ({
      countryCode: 'AU',
      currencyCode: 'AUD',
      supportedNetworks: APPLE_PAY_SUPPORTED_NETWORKS,
      merchantCapabilities: APPLE_PAY_MERCHANT_CAPABILITIES,
      total: {
        label: APPLE_PAY_LABEL,
        amount: formatDollars(amountCents),
      },
    }),
    [amountCents],
  );

  const validateMerchant = useCallback(
    async (validationURL: string): Promise<ApplePayMerchantSession> => {
      return (await convex.action(api.payment.payments.validateApplePayMerchant, {
        sessionUuid,
        validationURL,
      })) as ApplePayMerchantSession;
    },
    [convex, sessionUuid],
  );

  const authorizePayment = useCallback(
    async (payment: ApplePayPayment): Promise<ApplePayAuthorizationResult> => {
      const token = getWorldlineApplePayToken(payment);
      const result = await convex.action(api.payment.payments.createApplePayPayment, {
        sessionUuid,
        amountCents,
        ...token,
      });

      if (result.status === 'SUCCESS') {
        return { status: 'SUCCESS' };
      }

      return {
        status: 'FAILED',
        errorMessage: result.errorMessage,
      };
    },
    [amountCents, convex, sessionUuid],
  );

  return (
    <ApplePayButton
      version={APPLE_PAY_BUTTON_VERSION}
      paymentRequest={paymentRequest}
      validateMerchant={validateMerchant}
      authorizePayment={authorizePayment}
      onSuccess={onSuccess}
      onError={onError}
      onSubmitting={onSubmitting}
      onCancel={onCancel}
    />
  );
};
